import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TNil } from '@app/app.interface';
import { ICtaForm, ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { EAdvancedSettings } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-advanced-settings.interface';
import { TExecutionApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { ECustomFieldType, ICustomField, ICustomValueConfig } from '@core/directives/custom-cta-base.directive';
import {
  CTA_TEMPLATE_DELIVERABLE_CUSTOM_VALUE_STEPS,
  ICustomCtaValues,
  IEntityWithStoredCustomValues,
  IFormWithCustomValues,
} from '@core/interfaces/custom-values.interface';
import { DeliverableChangeTrackerService } from '@core/services/deliverable-change-tracker.service';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { ExecutionChangeTrackerService } from '@core/services/execution-change-tracker.service';
import { EDeliverableType, EOverwriteStatus } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { assertNever } from '@utilities/type-utils';
import { camelCase, get, set, snakeCase } from 'lodash';
import { debounceTime, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

// For #8/#9 (campaign_creation_deliverable_changed / _advanced_settings_changed) - lets
// startFormValueMonitoring() report the parent deliverable's id/type even when the change comes
// from a nested-array item (e.g. one keyword's text field), which has no id/type of its own.
export interface IDeliverableFieldChangeTrackingContext {
  kind: 'deliverable';
  getCtaTemplateId: () => string | undefined;
  getDeliverableId: () => string | undefined;
  deliverableType: EDeliverableType;
  // Round-3 audit - keyword-tracking rows (section 3) aren't a flow-dialog step, so their
  // deliverable events omit campaignCreationCurrentStep.
  isGlobalTracking: boolean;
}

// Executions are their own top-level FormArray (not a deliverable), so their change events carry
// executionId instead of deliverableId/deliverableType - see event.interface.ts's executionId field.
export interface IExecutionFieldChangeTrackingContext {
  kind: 'execution';
  getCtaTemplateId: () => string | undefined;
  getExecutionId: () => string | undefined;
}

// KPIs have no add/remove UI (every available kpi type is auto-synced onto the form with an empty
// value - see ExpectedKpiListComponent.syncExpectedKpisWithAvailableKpis()), so #10/#11-style
// kpi_added/_removed are derived from the expectedValue field's empty<->filled transitions rather
// than from a structural add/remove call, unlike deliverables/executions.
export interface IKpiFieldChangeTrackingContext {
  kind: 'kpi';
  getCtaTemplateId: () => string | undefined;
  getExecutionId: () => string | undefined;
  getKpi: () => TExpectedKpi | null;
  getExpectedKpiId: () => string | undefined;
}

export type TFieldChangeTrackingContext =
  | IDeliverableFieldChangeTrackingContext
  | IExecutionFieldChangeTrackingContext
  | IKpiFieldChangeTrackingContext;

// Bundles startFormValueMonitoring()'s per-call args into one object (max-params) - itemIndex is
// only set for nested-array items, to disambiguate a field name that's shared across every row.
export interface IFieldMonitoringOptions {
  creatorId: string | TNil;
  isCustomMode: () => boolean;
  trackingContext: TFieldChangeTrackingContext;
  itemIndex?: number;
}

@Injectable({
  providedIn: 'root',
})
export class CustomValuesService {
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly deliverableChangeTracker = inject(DeliverableChangeTrackerService);
  private readonly executionChangeTracker = inject(ExecutionChangeTrackerService);
  private readonly eventTrackingService = inject(EventTrackingService);

  // LF-3 (audit 2026-08-06): field-change commits pending inside their 800ms debounce window,
  // registered so a save can flush them synchronously. Without this, a save landing before the
  // debounce fires would persist the edit but lose both the immediate event (the pending debounce
  // is torn down with the form rebuild after save) and the dirty mark feeding the _saved diff.
  private readonly pendingTrackingCommits = new Set<() => void>();

  public initializeGlobalValues(form: FormGroup, fields: ICustomField[]): void {
    if (!form.get('globalCtaValues')) {
      form.addControl('globalCtaValues', new FormControl<IGlobalCtaValue[] | null>(null));
    }

    const formValue = form.value as IFormWithCustomValues;

    if (!formValue.globalCtaValues?.length) {
      const globalValues: IGlobalCtaValue[] = [];

      fields.forEach((field) => {
        if (field.isMonitoringDisabled) {
          return;
        }

        const value = form.get(field.name)?.value;

        globalValues.push({
          value:
            field.type === ECustomFieldType.date && value ? this.dateUtils.formatToApiDateTime(value as Date) : value,
          valueName: snakeCase(field.name),
        });
      });

      form.get('globalCtaValues')?.setValue(globalValues, { emitEvent: false });
    }
  }

  public switchToCustomMode(form: FormGroup, fields: ICustomField[], creatorId: string, isViewMode = false): void {
    if (form.get('exclusiveTo')?.value) {
      return;
    }

    const formValue = form.value as IFormWithCustomValues;
    const customValues =
      formValue.customCtaValues?.filter((value: ICustomCtaValue) => value.creatorId === creatorId) ?? [];

    fields.forEach((field) => {
      this.applyCustomModeFieldState(form, field, customValues, creatorId, isViewMode);
    });
  }

  private applyCustomModeFieldState(
    form: FormGroup,
    field: ICustomField,
    customValues: ICustomCtaValue[],
    creatorId: string,
    isViewMode: boolean,
  ): void {
    const customValue = customValues.find((value: ICustomCtaValue) => value.valueName === snakeCase(field.name));
    // The deleted marker is identified by its null valueName (or the legacy 'id' variant) - the
    // status check was dropped because the backend's save echo can serialize the status
    // differently, and the KPI count + revert-icon logic are already status-insensitive, so the
    // display must match them (no other record ever uses a null valueName).
    const deletedCustomValue = customValues.find(
      (value: ICustomCtaValue) => value.valueName === null || value.valueName === 'id',
    );
    const globalValue = (form.value as IFormWithCustomValues).globalCtaValues?.find(
      (value: IGlobalCtaValue) => value.valueName === snakeCase(field.name),
    );
    const control = form.get(field.name);

    if (!control || field.name === 'id' || field.isMonitoringDisabled) {
      return;
    }

    // KPI rows never disable on a deleted marker - they render as an empty, editable value for
    // the creator the marker excludes (the global fallback would leak the shared value into it),
    // with the revert button as the restore affordance.
    if (!!form.get('kpi') && deletedCustomValue) {
      if (!isViewMode) {
        control.setValue(null, { emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
      }

      return;
    }

    let value: unknown = null;
    let hasStoredValue = false;

    if (customValue) {
      hasStoredValue = true;

      if (field.type === ECustomFieldType.date && customValue.value) {
        value = this.dateUtils.parseDateTimeFromApi(customValue.value);
      } else if (field.type === ECustomFieldType.boolean) {
        value = customValue.value === 'true';
      } else {
        ({ value } = customValue);
      }
    } else if (globalValue) {
      hasStoredValue = true;

      if (field.type === ECustomFieldType.date && globalValue.value) {
        value = this.dateUtils.parseDateTimeFromApi(globalValue.value);
      } else if (field.type === ECustomFieldType.boolean) {
        value = String(globalValue.value) === 'true';
      } else {
        ({ value } = globalValue);
      }
    } else if (field.type === ECustomFieldType.boolean) {
      // Preserve the form default for booleans when no stored value exists
      hasStoredValue = false;
    }

    if (deletedCustomValue) {
      control.disable({ emitEvent: false });
    } else if (!isViewMode) {
      control.enable({ emitEvent: false });
    }

    if (hasStoredValue) {
      control.setValue(value, { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  public switchToGlobalMode(form: FormGroup, fields: ICustomField[]): void {
    const formValue = form.value as IFormWithCustomValues;
    const globalValues = formValue.globalCtaValues ?? [];

    fields.forEach((field) => {
      const globalValue = globalValues.find((value: IGlobalCtaValue) => value.valueName === snakeCase(field.name));
      const control = form.get(field.name);

      if (!control || !!form.get('exclusiveTo')?.value || field.name === 'id' || field.isMonitoringDisabled) {
        return;
      }

      let value: unknown = null;
      let hasStoredValue = false;

      if (globalValue?.value !== undefined && globalValue.value !== null) {
        hasStoredValue = true;

        if (field.type === ECustomFieldType.date) {
          value = this.dateUtils.parseDateTimeFromApi(globalValue.value);
        } else if (field.type === ECustomFieldType.boolean) {
          value = String(globalValue.value) === 'true';
        } else {
          ({ value } = globalValue);
        }
      }

      if (hasStoredValue) {
        control.setValue(value, { emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
      } else if (field.type !== ECustomFieldType.boolean) {
        // A custom-mode value must not leak into global mode when no global value exists (e.g. a
        // KPI goal added for one creator) - reset to empty. Booleans keep their form default, see
        // switchToCustomMode's preserve-default branch.
        control.setValue(null, { emitEvent: false });
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  public startFormValueMonitoring(
    form: FormGroup,
    fields: ICustomField[],
    options: IFieldMonitoringOptions,
  ): () => void {
    const { creatorId, isCustomMode } = options;
    const trackingCleanups = fields.map((field) => this.createFieldChangeTrackingSubscription(form, field, options));

    const subscriptions = fields.map((field) =>
      this.createFieldBookkeepingSubscription(form, field, creatorId, isCustomMode, options.trackingContext),
    );

    return () => {
      subscriptions.forEach((subscription) => subscription?.unsubscribe());
      trackingCleanups.forEach((cleanup) => cleanup());
    };
  }

  // LF-3 - runs any field-change commits still inside their debounce window synchronously, so a
  // save never races the 800ms timer. Called by campaign-form.component.ts right before submitting
  // (processFormSubmission). A flushed commit fires the immediate event and marks dirty; the
  // debounced subscription that would have fired later no-ops on the already-committed value.
  public flushPendingTracking(): void {
    for (const pendingCommit of Array.from(this.pendingTrackingCommits)) {
      pendingCommit();
    }
  }

  // For structural nested-array changes (add/remove a requirement or image row) that a plain
  // valueChanges subscription can't report - on remove, the field-change subscription for that row
  // is already torn down (cleanupNestedForm) before the row is spliced out, so nothing would
  // otherwise fire.
  //
  // trackDeliverableItemAdded/Removed (not one shared method) because the dirty-tracking side needs
  // to differ: an add always marks dirty AND records the source as "added this session"; a remove
  // only marks dirty if the row wasn't itself added this same session - if it was (add-then-remove
  // before ever saving), the pair nets out to no change and must NOT leave a dirty flag behind, or
  // #8b could fire changed_saved for a deliverable nothing actually happened to (found in manual QA
  // 2026-08-03's review of issue #3 - the same class of bug #8b's field-level revert-tracking
  // already guards against, just with no scalar value to compare here).
  public trackDeliverableItemAdded(
    options: Omit<IFieldMonitoringOptions, 'itemIndex' | 'trackingContext'> & {
      trackingContext: IDeliverableFieldChangeTrackingContext;
    },
    source: string,
  ): void {
    const deliverableId = options.trackingContext.getDeliverableId();

    this.deliverableChangeTracker.markFieldChanged(deliverableId, source);
    this.deliverableChangeTracker.markItemAddedThisSession(deliverableId, source);
    this.trackDeliverableChangedEvent(options, source, undefined, undefined);
  }

  public trackDeliverableItemRemoved(
    options: Omit<IFieldMonitoringOptions, 'itemIndex' | 'trackingContext'> & {
      trackingContext: IDeliverableFieldChangeTrackingContext;
    },
    source: string,
    removedValue: string | undefined,
  ): void {
    const deliverableId = options.trackingContext.getDeliverableId();
    const wasAddedThisSession = this.deliverableChangeTracker.consumeItemAddedThisSession(deliverableId, source);

    if (wasAddedThisSession) {
      this.deliverableChangeTracker.markFieldUnchanged(deliverableId, source);
    } else {
      this.deliverableChangeTracker.markFieldChanged(deliverableId, source);
    }

    this.trackDeliverableChangedEvent(options, source, removedValue, undefined);
  }

  // Round-4 review (2026-08-06) - the nested-row per-creator checkbox path (CustomCtaBaseDirective's
  // nestedFormCheckboxChange('id')): the immediate deliverable_changed fires on every marker toggle
  // (mirroring the structural add/remove path), but dirty-tracking must reflect the NET marker state
  // vs the session baseline - the marker can be applied and removed again before saving (uncheck
  // then re-check = zero net change), and a row that nets out unchanged must not stay dirty or
  // changed_saved would fire for something the user didn't actually change.
  public trackDeliverableMarkerToggle(
    options: Omit<IFieldMonitoringOptions, 'itemIndex' | 'trackingContext'> & {
      trackingContext: IDeliverableFieldChangeTrackingContext;
    },
    source: string,
    isNetChange: boolean,
  ): void {
    const deliverableId = options.trackingContext.getDeliverableId();

    if (isNetChange) {
      this.deliverableChangeTracker.markFieldChanged(deliverableId, source);
    } else {
      this.deliverableChangeTracker.markFieldUnchanged(deliverableId, source);
    }

    this.trackDeliverableChangedEvent(options, source, undefined, undefined);
  }

  private trackDeliverableChangedEvent(
    options: Omit<IFieldMonitoringOptions, 'itemIndex' | 'trackingContext'> & {
      trackingContext: IDeliverableFieldChangeTrackingContext;
    },
    source: string,
    previousValue: string | undefined,
    newValue: string | undefined,
  ): void {
    const { creatorId, isCustomMode, trackingContext } = options;

    this.eventTrackingService.track(EEventType.campaignCreationDeliverableChanged, {
      campaignCreationCurrentStep: trackingContext.isGlobalTracking ? undefined : 2,
      campaignCreationIsOverride: isCustomMode(),
      creatorId: creatorId ?? undefined,
      ctaTemplateId: trackingContext.getCtaTemplateId(),
      deliverableId: trackingContext.getDeliverableId(),
      deliverableType: trackingContext.deliverableType,
      newValue,
      previousValue,
      source,
    });
  }

  private createFieldBookkeepingSubscription(
    form: FormGroup,
    field: ICustomField,
    creatorId: string | TNil,
    isCustomMode: () => boolean,
    trackingContext: TFieldChangeTrackingContext,
  ): Subscription | null {
    const control = form.get(field.name);

    if (!control || field.isMonitoringDisabled) {
      return null;
    }

    return control.valueChanges.subscribe((currentValue) => {
      const formValue = form.value as IFormWithCustomValues;
      const exclusiveToControl = form.get('exclusiveTo');

      if (exclusiveToControl?.value) {
        form.patchValue({ customCtaValues: [] }, { emitEvent: false });

        return;
      }

      if (isCustomMode()) {
        this.updateCustomModeBookkeeping(form, field, creatorId, currentValue, trackingContext);
      } else {
        const globalValues: IGlobalCtaValue[] = [...(formValue.globalCtaValues ?? [])];
        const existingGlobalValueIndex = globalValues.findIndex(
          (value: IGlobalCtaValue) =>
            value.valueName === snakeCase(field.name) || (value.valueName === null && field.name === 'id'),
        );
        const value = this.getMonitoredFieldValue(field.type, currentValue);

        if (existingGlobalValueIndex !== -1) {
          // Replace (never mutate in place) - see the custom-mode branch's comment.
          globalValues[existingGlobalValueIndex] = {
            ...globalValues[existingGlobalValueIndex],
            value,
          };
        } else {
          globalValues.push({ value, valueName: snakeCase(field.name) });
        }

        form.patchValue({ globalCtaValues: globalValues }, { emitEvent: false });
      }
    });
  }

  private updateCustomModeBookkeeping(
    form: FormGroup,
    field: ICustomField,
    creatorId: string | TNil,
    currentValue: unknown,
    trackingContext: TFieldChangeTrackingContext,
  ): void {
    if (!creatorId) {
      return;
    }

    const customValues: ICustomCtaValue[] = [...((form.value as IFormWithCustomValues).customCtaValues ?? [])];
    const existingCustomValueIndex = customValues.findIndex(
      (value: ICustomCtaValue) =>
        value.creatorId === creatorId &&
        (value.valueName === snakeCase(field.name) || (value.valueName === null && field.name === 'id')),
    );
    const value = this.getMonitoredFieldValue(field.type, currentValue);
    const isKpiExpectedValue = trackingContext.kind === 'kpi' && snakeCase(field.name) === 'expected_value';

    if (isKpiExpectedValue && this.isEmptyMonitoredValue(value)) {
      // An emptied expected value means the KPI is removed for this creator (see
      // trackKpiFieldChange's empty<->filled semantics), so the override is converted into
      // the deleted-marker (valueName null + status deleted) that customDelete uses
      // elsewhere - a plain overwritten-with-null record would leave the old value intact.
      if (existingCustomValueIndex !== -1) {
        customValues[existingCustomValueIndex] = {
          creatorId,
          id: customValues[existingCustomValueIndex].id,
          status: EOverwriteStatus.deleted,
          value: null,
          valueName: null,
        };
      } else {
        customValues.push({
          creatorId,
          id: uuidv4(),
          status: EOverwriteStatus.deleted,
          value: null,
          valueName: null,
        });
      }
    } else if (existingCustomValueIndex !== -1) {
      // Replace (never mutate in place): the dialog's form shares these record objects with the
      // campaign form's values, so an in-place write would survive a discarded dialog.
      customValues[existingCustomValueIndex] = {
        ...customValues[existingCustomValueIndex],
        value,
      };
    } else {
      if (isKpiExpectedValue) {
        // A fresh override supersedes a previously-marked deletion for this creator.
        const deletedMarkerIndex = customValues.findIndex(
          (customValue: ICustomCtaValue) => customValue.creatorId === creatorId && customValue.valueName === null,
        );

        if (deletedMarkerIndex !== -1) {
          customValues.splice(deletedMarkerIndex, 1);
        }
      }

      customValues.push({
        creatorId,
        id: uuidv4(),
        status: EOverwriteStatus.overwritten,
        value,
        valueName: snakeCase(field.name),
      });
    }

    form.patchValue({ customCtaValues: customValues ?? [] }, { emitEvent: false });
  }

  public hasPersistedValue(
    formValue: IEntityWithStoredCustomValues,
    fieldName: string,
    isValueSet: (value: unknown) => boolean = this.isValueSet,
  ): boolean {
    if (isValueSet(get(formValue, fieldName))) {
      return true;
    }

    if (this.hasSetMonitoredValue(formValue.customCtaValues, fieldName, isValueSet)) {
      return true;
    }

    return this.hasSetMonitoredValue(formValue.globalCtaValues, fieldName, isValueSet);
  }

  public revertToGlobalValue(
    form: FormGroup | undefined,
    fieldName: string,
    creatorId: string,
    fieldType: ECustomFieldType,
  ): void {
    if (!form || !fieldName) {
      return;
    }

    const fieldControl = form.get(fieldName);
    const customCtaValues = form.get('customCtaValues');
    const customValueIndex = (customCtaValues?.value as ICustomCtaValue[])?.findIndex(
      (value) =>
        value.creatorId === creatorId &&
        (value.valueName === snakeCase(fieldName) ||
          // A deleted-marker row (null valueName - status-insensitive, see the custom-mode
          // branch's comment) is reverted the same way as a value override - remove the marker
          // and restore the global value.
          value.valueName === null),
    );
    const customValue = (customCtaValues?.value as ICustomCtaValue[])?.[customValueIndex];
    const globalValue = (form.get('globalCtaValues')?.value as IGlobalCtaValue[])?.find(
      (value) => value.valueName === snakeCase(fieldName),
    );

    if (customValue && globalValue) {
      if (fieldType === ECustomFieldType.date && globalValue.value) {
        fieldControl?.setValue(this.dateUtils.parseDateTimeFromApi(globalValue.value));
      } else if (fieldType === ECustomFieldType.boolean) {
        fieldControl?.setValue(String(globalValue.value) === 'true');
      } else {
        fieldControl?.setValue(globalValue.value);
      }

      // Removed by predicate, not by the index captured above: the field setValue fires the
      // bookkeeping subscription (custom-mode typing), which rewrites the customCtaValues array -
      // a stale index would then remove another creator's record and leave a freshly re-created
      // override behind, so the revert button stayed visible for a second click.
      customCtaValues?.setValue(
        (customCtaValues?.value as ICustomCtaValue[]).filter(
          (value) =>
            !(
              value.creatorId === creatorId &&
              (value.valueName === snakeCase(fieldName) ||
                (value.valueName === null && value.status === EOverwriteStatus.deleted))
            ),
        ),
      );

      // A revert is a user change like any edit - programmatic setValue doesn't mark the form
      // dirty on its own, so without this the discard dialog never arms for it.
      form.markAsDirty();
    }
  }

  public customDelete(
    customValueConfig: ICustomValueConfig,
    creatorId: string,
    fieldName: string,
    index?: number,
  ): void {
    if (
      customValueConfig.componentFields &&
      customValueConfig.componentFields.managedFields.some((field) => field.name === snakeCase(fieldName)) &&
      index === undefined
    ) {
      const form = customValueConfig.componentFields.getForm();
      const formValue = form.value as IFormWithCustomValues;
      const customValues: ICustomCtaValue[] = [...(formValue.customCtaValues ?? [])];
      const existingCustomValueIndex = customValues.findIndex(
        (value) =>
          value.creatorId === creatorId && (value.valueName === snakeCase(fieldName) || value.valueName === null),
      );

      if (existingCustomValueIndex === -1) {
        customValues.push({
          creatorId,
          id: uuidv4(),
          status: EOverwriteStatus.deleted,
          value: null,
          valueName: null,
        });
      } else {
        customValues.splice(existingCustomValueIndex, 1);
      }

      form.patchValue({ customCtaValues: customValues });
    }

    if (customValueConfig.nestedArray && typeof index === 'number') {
      const { nestedArray } = customValueConfig;

      if (nestedArray) {
        const form = nestedArray.getFormArray().at(index);
        const formValue = form.value as IFormWithCustomValues;
        const customValues: ICustomCtaValue[] = [...(formValue.customCtaValues ?? [])];
        const existingCustomValueIndex = customValues.findIndex(
          (value) =>
            value.creatorId === creatorId && (value.valueName === snakeCase(fieldName) || value.valueName === null),
        );

        if (existingCustomValueIndex === -1) {
          customValues.push({
            creatorId,
            id: uuidv4(),
            status: EOverwriteStatus.deleted,
            value: null,
            valueName: null,
          });
          form.disable({ emitEvent: false });
        } else {
          customValues.splice(existingCustomValueIndex, 1);
          form.enable({ emitEvent: false });
        }

        // Emits (no emitEvent:false) so the parent FormArray's valueChanges fires and the flow
        // dialog's discard-arm subscription sees the toggle - a suppressed patch would leave the
        // form pristine and the "Discard changes?" dialog would never arm (see
        // DeliverableFlowDialogComponent.armDiscardDialog).
        nestedArray
          .getFormArray()
          .at(index)
          .patchValue({ customCtaValues: customValues ?? [] });
      }
    }
  }

  public getCtaWithCreatorCustomValues(cta: ICtaTemplateApi, creatorId: string): ICtaTemplateApi {
    const ctaClone = structuredClone(cta);
    ctaClone.name = this.getCreatorCustomValue<string>(cta, creatorId, 'name') ?? cta.name;

    this.replaceAllWithCustomValues(ctaClone, creatorId);

    return ctaClone;
  }

  public getCreatorCustomValue<T>(
    cta: { customCtaValues?: ICustomCtaValue[] | undefined },
    creatorId: string,
    valueName: string | null,
    getValueOnly = true,
  ): T | TNil {
    const customValue = cta.customCtaValues?.find(
      (value) => value.creatorId === creatorId && value.valueName === valueName,
    );

    if (!customValue) {
      return undefined;
    }

    return (getValueOnly ? customValue.value : customValue) as T | null;
  }

  public getCreatorCustomValues<T>(
    cta: { customCtaValues?: ICustomCtaValue[] | undefined },
    creatorId: string,
    wrapAsObject = false,
  ): T {
    const creatorFilteredCustomCtaValues = cta.customCtaValues?.filter((value) => value.creatorId === creatorId) ?? [];

    return (wrapAsObject ? { customCtaValues: creatorFilteredCustomCtaValues } : creatorFilteredCustomCtaValues) as T;
  }

  public replaceWithCustomValues<T extends ICustomCtaValues>(
    creatorId: string,
    entityWithCustomValues: T[] | null | undefined,
    nestedEntityKey?: string,
  ): T[] {
    return (entityWithCustomValues ?? []).reduce((acc: T[], entity) => {
      const idCustomValue = this.getCreatorCustomValue<ICustomCtaValue>(entity, creatorId, null, false);

      if (
        (idCustomValue?.valueName === null && idCustomValue.status === EOverwriteStatus.deleted) ||
        (entity.exclusiveTo && entity.exclusiveTo !== creatorId)
      ) {
        return acc;
      }

      const customValues = this.getCreatorCustomValues<ICustomCtaValue[]>(entity, creatorId);

      customValues.forEach((customValue) => {
        if (!customValue.valueName) {
          return;
        }

        set(entity, camelCase(customValue.valueName), customValue.value);
      });

      if (nestedEntityKey) {
        set(
          entity,
          nestedEntityKey,
          this.replaceWithCustomValues<ICustomCtaValues>(creatorId, get(entity, nestedEntityKey)),
        );
      }

      acc.push(entity);

      return acc;
    }, []);
  }

  public removeCreatorsCustomValues(control: FormGroup<ICtaForm>, creatorId: string): void {
    control
      .get('customCtaValues')
      ?.setValue(control.get('customCtaValues')?.value?.filter((value) => value.creatorId !== creatorId) ?? [], {
        emitEvent: false,
      });

    control.controls.executions.controls.forEach((execution) => {
      execution
        .get('customCtaValues')
        ?.setValue(execution.get('customCtaValues')?.value?.filter((value) => value.creatorId !== creatorId) ?? [], {
          emitEvent: false,
        });
    });

    control.controls.deliverables.controls.forEach((deliverable) => {
      deliverable
        .get('customCtaValues')
        ?.setValue(deliverable.get('customCtaValues')?.value?.filter((value) => value.creatorId !== creatorId) ?? [], {
          emitEvent: false,
        });
    });
  }

  private replaceAllWithCustomValues(ctaClone: ICtaTemplateApi, creatorId: string): void {
    ctaClone.executions = this.replaceWithCustomValues<TExecutionApi>(creatorId, ctaClone.executions, 'expectedKpis');

    for (const step of CTA_TEMPLATE_DELIVERABLE_CUSTOM_VALUE_STEPS) {
      this.replaceDeliverableCollectionWithCustomValues(ctaClone, creatorId, step);
    }
  }

  private replaceDeliverableCollectionWithCustomValues(
    ctaClone: ICtaTemplateApi,
    creatorId: string,
    step: (typeof CTA_TEMPLATE_DELIVERABLE_CUSTOM_VALUE_STEPS)[number],
  ): void {
    switch (step.key) {
      case 'audioTrackings':
        ctaClone.audioTrackings = this.replaceWithCustomValues(
          creatorId,
          ctaClone.audioTrackings,
          step.nestedEntityKey,
        );
        break;
      case 'captions':
        ctaClone.captions = this.replaceWithCustomValues(creatorId, ctaClone.captions, step.nestedEntityKey);
        break;
      case 'chatbots':
        ctaClone.chatbots = this.replaceWithCustomValues(creatorId, ctaClone.chatbots);
        break;
      case 'chatbotMultis':
        ctaClone.chatbotMultis = this.replaceWithCustomValues(creatorId, ctaClone.chatbotMultis);
        break;
      case 'contents':
        ctaClone.contents = this.replaceWithCustomValues(creatorId, ctaClone.contents, step.nestedEntityKey);
        break;
      case 'customs':
        ctaClone.customs = this.replaceWithCustomValues(creatorId, ctaClone.customs, step.nestedEntityKey);
        break;
      case 'descriptions':
        ctaClone.descriptions = this.replaceWithCustomValues(creatorId, ctaClone.descriptions, step.nestedEntityKey);
        break;
      case 'gamePlays':
        ctaClone.gamePlays = this.replaceWithCustomValues(creatorId, ctaClone.gamePlays);
        break;
      case 'hashTags':
        ctaClone.hashTags = this.replaceWithCustomValues(creatorId, ctaClone.hashTags, step.nestedEntityKey);
        break;
      case 'mentions':
        ctaClone.mentions = this.replaceWithCustomValues(creatorId, ctaClone.mentions, step.nestedEntityKey);
        break;
      case 'messages':
        ctaClone.messages = this.replaceWithCustomValues(creatorId, ctaClone.messages, step.nestedEntityKey);
        break;
      case 'productPlacements':
        ctaClone.productPlacements = this.replaceWithCustomValues(
          creatorId,
          ctaClone.productPlacements,
          step.nestedEntityKey,
        );
        break;
      case 'showcases':
        ctaClone.showcases = this.replaceWithCustomValues(creatorId, ctaClone.showcases, step.nestedEntityKey);
        break;
      case 'talkingPointAis':
        ctaClone.talkingPointAis = this.replaceWithCustomValues(creatorId, ctaClone.talkingPointAis);
        break;
      case 'talkingPoints':
        ctaClone.talkingPoints = this.replaceWithCustomValues(creatorId, ctaClone.talkingPoints, step.nestedEntityKey);
        break;
      case 'textTrackings':
        ctaClone.textTrackings = this.replaceWithCustomValues(creatorId, ctaClone.textTrackings, step.nestedEntityKey);
        break;
      case 'titles':
        ctaClone.titles = this.replaceWithCustomValues(creatorId, ctaClone.titles, step.nestedEntityKey);
        break;
      case 'visuals':
        ctaClone.visuals = this.replaceWithCustomValues(creatorId, ctaClone.visuals);
        break;
      default:
        assertNever(step);
    }
  }

  // #8/#9 fire from a separate debounced subscription (not the bookkeeping one in
  // startFormValueMonitoring), so the existing customCtaValues/globalCtaValues patching is never
  // delayed - "field commit, not per-keystroke" per the doc, since none of these controls gate on
  // blur today. Debounce alone gives that semantic: rapid keystrokes collapse into one fire per
  // typing pause.
  //
  // #8 (deliverable_changed) and #9 (advanced_settings_changed) are mutually exclusive per field,
  // not paired 1:1 - a field only fires one or the other, decided by whether its name is one of the
  // known EAdvancedSettings values (currently just sponsoredContentRelevant). Both carry the same
  // property shape (new/previous value, source, isOverride) - see manual QA findings 2026-08-03.
  //
  // Branches on trackingContext.kind for executions (Wave 9) - same debounced commit-point and
  // dirty-tracking shape as deliverables, but firing campaign_creation_execution_changed with
  // executionId instead of deliverableId/deliverableType (executions are their own FormArray, not a
  // deliverable - see event.interface.ts's executionId field).
  private createFieldChangeTrackingSubscription(
    form: FormGroup,
    field: ICustomField,
    { creatorId, isCustomMode, trackingContext, itemIndex }: IFieldMonitoringOptions,
  ): () => void {
    const control = form.get(field.name);

    if (!control || field.isMonitoringDisabled) {
      return () => undefined;
    }

    // Nested array items (e.g. a title's 2nd keyword) share one field name across every row, so the
    // 1-based index disambiguates both the reported `source` and the per-field dirty-tracking key
    // used by #8b - without it, reverting one row's value would incorrectly clear another row's
    // still-genuine dirty flag (they'd otherwise collide on the same field name).
    const source = itemIndex === undefined ? snakeCase(field.name) : `${snakeCase(field.name)}_${itemIndex + 1}`;
    const isAdvancedSetting = (Object.values(EAdvancedSettings) as string[]).includes(field.name);
    const originalValue = this.getMonitoredFieldValue(field.type, control.value);
    let lastTrackedValue = originalValue;

    // LF-3 - extracted from the debounced subscription so the same commit path (dirty-marking +
    // event fire) can run either on the typing-pause debounce or synchronously at save time via
    // flushPendingTracking(). See the pendingTrackingCommits field's comment.
    const commit = (currentValue: unknown): void => {
      const newValue = this.getMonitoredFieldValue(field.type, currentValue);

      if (newValue === lastTrackedValue) {
        return;
      }

      const values = { newValue, originalValue, previousValue: lastTrackedValue };

      if (trackingContext.kind === 'execution') {
        this.trackExecutionFieldChange(trackingContext, source, values, { creatorId, isCustomMode });
      } else if (trackingContext.kind === 'kpi') {
        this.trackKpiFieldChange(trackingContext, values, { creatorId, isCustomMode });
      } else {
        this.trackDeliverableFieldChange(trackingContext, source, isAdvancedSetting, values, {
          creatorId,
          isCustomMode,
        });
      }

      lastTrackedValue = newValue;
    };

    // Reads the control's latest value at flush time (a valueChanges emission may have been
    // swallowed by the debounce since the last commit).
    const pendingCommit = (): void => commit(control.value);

    const debouncedSubscription = control.valueChanges.pipe(debounceTime(800)).subscribe((currentValue) => {
      this.pendingTrackingCommits.delete(pendingCommit);
      commit(currentValue);
    });

    // Arms the pending-commit registry as soon as a change enters the debounce window (the
    // debounced subscription only observes the value after the 800ms elapse).
    const pendingMarkerSubscription = control.valueChanges.subscribe(() => {
      if (!this.pendingTrackingCommits.has(pendingCommit)) {
        this.pendingTrackingCommits.add(pendingCommit);
      }
    });

    return () => {
      debouncedSubscription.unsubscribe();
      pendingMarkerSubscription.unsubscribe();
      this.pendingTrackingCommits.delete(pendingCommit);
    };
  }

  // Compared against originalValue (session-start), not previousValue (last fire) - an edit that's
  // later reverted back to its starting value must stop counting as dirty, or #8b could fire
  // "changed_saved" for an execution that nets out unchanged. See
  // DeliverableChangeTrackerService's own comment for why this is tracked per-field.
  private trackExecutionFieldChange(
    trackingContext: IExecutionFieldChangeTrackingContext,
    source: string,
    {
      newValue,
      originalValue,
      previousValue,
    }: { newValue: string | null; originalValue: string | null; previousValue: string | null },
    { creatorId, isCustomMode }: Pick<IFieldMonitoringOptions, 'creatorId' | 'isCustomMode'>,
  ): void {
    const executionId = trackingContext.getExecutionId();

    if (newValue === originalValue) {
      this.executionChangeTracker.markFieldUnchanged(executionId, source);
    } else {
      this.executionChangeTracker.markFieldChanged(executionId, source);
    }

    this.eventTrackingService.track(EEventType.campaignCreationExecutionChanged, {
      campaignCreationCurrentStep: 3,
      campaignCreationIsOverride: isCustomMode(),
      creatorId: creatorId ?? undefined,
      ctaTemplateId: trackingContext.getCtaTemplateId(),
      executionId,
      newValue: newValue ?? undefined,
      previousValue: previousValue ?? undefined,
      source,
    });
  }

  // No add/remove UI for KPIs (see IKpiFieldChangeTrackingContext's comment), so added/removed/
  // changed is decided by the expectedValue field's empty<->filled transition instead of a
  // structural call: previously empty and now filled -> added; previously filled and now empty ->
  // removed; filled on both sides but a different value -> changed. Dirty-tracking (for #66's
  // kpi_changed_saved) is still compared against originalValue (session-start), same rationale as
  // trackExecutionFieldChange/trackDeliverableFieldChange - it's a separate concern from which
  // immediate event fires.
  private trackKpiFieldChange(
    trackingContext: IKpiFieldChangeTrackingContext,
    {
      newValue,
      originalValue,
      previousValue,
    }: { newValue: string | null; originalValue: string | null; previousValue: string | null },
    { creatorId, isCustomMode }: Pick<IFieldMonitoringOptions, 'creatorId' | 'isCustomMode'>,
  ): void {
    const executionId = trackingContext.getExecutionId();
    const expectedKpiId = trackingContext.getExpectedKpiId();
    const source = snakeCase(trackingContext.getKpi() ?? '');
    const wasEmpty = this.isEmptyMonitoredValue(previousValue);
    const isEmpty = this.isEmptyMonitoredValue(newValue);

    let eventType = EEventType.campaignCreationKpiChanged;

    if (wasEmpty && !isEmpty) {
      eventType = EEventType.campaignCreationKpiAdded;
    } else if (!wasEmpty && isEmpty) {
      eventType = EEventType.campaignCreationKpiRemoved;
    }

    if (newValue === originalValue) {
      this.executionChangeTracker.markKpiUnchanged(executionId, source);
    } else {
      this.executionChangeTracker.markKpiChanged(executionId, source);
    }

    this.eventTrackingService.track(eventType, {
      campaignCreationCurrentStep: 4,
      campaignCreationIsOverride: isCustomMode(),
      creatorId: creatorId ?? undefined,
      ctaTemplateId: trackingContext.getCtaTemplateId(),
      executionId,
      expectedKpiId,
      newValue: isEmpty ? undefined : (newValue ?? undefined),
      previousValue: wasEmpty ? undefined : (previousValue ?? undefined),
      source,
    });
  }

  // Compared against originalValue (session-start), not previousValue (last fire) - an edit that's
  // later reverted back to its starting value must stop counting as dirty, or #8b could fire
  // "changed_saved" for a deliverable that nets out unchanged. See
  // DeliverableChangeTrackerService's own comment for why this is tracked per-field.
  private trackDeliverableFieldChange(
    trackingContext: IDeliverableFieldChangeTrackingContext,
    source: string,
    isAdvancedSetting: boolean,
    {
      newValue,
      originalValue,
      previousValue,
    }: { newValue: string | null; originalValue: string | null; previousValue: string | null },
    { creatorId, isCustomMode }: Pick<IFieldMonitoringOptions, 'creatorId' | 'isCustomMode'>,
  ): void {
    const { getDeliverableId, deliverableType } = trackingContext;
    const deliverableId = getDeliverableId();

    if (newValue === originalValue) {
      this.deliverableChangeTracker.markFieldUnchanged(deliverableId, source);
    } else {
      this.deliverableChangeTracker.markFieldChanged(deliverableId, source);
    }

    this.eventTrackingService.track(
      isAdvancedSetting
        ? EEventType.campaignCreationDeliverableAdvancedSettingsChanged
        : EEventType.campaignCreationDeliverableChanged,
      {
        campaignCreationCurrentStep: trackingContext.isGlobalTracking ? undefined : 2,
        campaignCreationIsOverride: isCustomMode(),
        creatorId: creatorId ?? undefined,
        ctaTemplateId: trackingContext.getCtaTemplateId(),
        deliverableId,
        deliverableType,
        newValue: newValue ?? undefined,
        previousValue: previousValue ?? undefined,
        source,
      },
    );
  }

  private getMonitoredFieldValue(fieldType: ECustomFieldType, currentValue: unknown): string | null {
    if (fieldType === ECustomFieldType.date && currentValue) {
      return this.dateUtils.formatToApiDateTime(currentValue as Date);
    }

    if (fieldType === ECustomFieldType.boolean || fieldType === ECustomFieldType.number) {
      return currentValue !== null && currentValue !== undefined ? String(currentValue) : null;
    }

    return (currentValue as string | null | undefined) ?? null;
  }

  private isEmptyMonitoredValue(value: string | null): boolean {
    return value === null || value === '';
  }

  private hasSetMonitoredValue(
    values: Array<ICustomCtaValue | IGlobalCtaValue> | null | undefined,
    fieldName: string,
    isValueSet: (value: unknown) => boolean,
  ): boolean {
    if (!values?.length) {
      return false;
    }

    const monitoredFieldName = snakeCase(fieldName);

    return values.some((value: ICustomCtaValue | IGlobalCtaValue) => {
      return value.valueName === monitoredFieldName && isValueSet(value.value);
    });
  }

  private isValueSet(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }
}
